/**
 * CodeBlock Component
 *
 * Mantine-integrated code highlighting component using @mantine/code-highlight
 * Replaces the legacy react-code-blocks implementation
 */

import React from 'react';
import { CodeHighlight } from '@mantine/code-highlight';
import { Code } from '@mantine/core';
import classes from './CodeBlock.module.css';
import type { CodeBlockProps } from '../types';

const STREAMING_CURSOR = '\u258C';

const isTsvContent = (language: string, content: string): boolean => {
  const normalizedLanguage = language.toLowerCase();
  if (normalizedLanguage === 'tsv') return true;
  if (normalizedLanguage !== 'text') return false;

  const isStreaming = content.endsWith(STREAMING_CURSOR);
  const contentToInspect = isStreaming
    ? content.slice(0, -STREAMING_CURSOR.length)
    : content;
  const rows = contentToInspect
    .split(/\r?\n/)
    .filter((row) => row.trim().length > 0);

  if (rows.length < 2) return false;

  const columnCounts = rows.map((row) => row.split('\t').length);
  const expectedColumnCount = columnCounts[0];
  const lastRowIsIncomplete = isStreaming
    && columnCounts[columnCounts.length - 1] < expectedColumnCount;
  const completedColumnCounts = lastRowIsIncomplete
    ? columnCounts.slice(0, -1)
    : columnCounts;

  return expectedColumnCount > 1
    && completedColumnCounts.length >= 2
    && completedColumnCounts.every(
      (columnCount) => columnCount === expectedColumnCount,
    );
};

export const CodeBlock: React.FC<CodeBlockProps> = ({
  language = 'text',
  children,
  inline = false
}) => {
  if (inline) {
    return (
      <Code className={classes.inlineCode}>
        {children}
      </Code>
    );
  }

  const blockClassName = isTsvContent(language, children)
    ? `${classes.codeBlock} ${classes.tsvCodeBlock}`
    : classes.codeBlock;

  return (
    <CodeHighlight
      code={children}
      language={language}
      withCopyButton
      className={blockClassName}
    />
  );
};
